import { useEffect } from 'react'
import { MessageSquare, Sparkles, X } from 'lucide-react'
import ModalPortal from './ModalPortal'
import { DiscordIcon, DISCORD_URL } from './DiscordIcon'
import { trackSegmentEvent } from '../lib/segment'
import './ChatHelpOverlay.css'

// Example questions surfaced in the overlay. When a project chat is open, clicking one drops it into
// the chat and sends it (see `onSelectExample`); otherwise they read as illustrative prompts.
const EXAMPLE_PROMPTS = [
  'What can you help me with?',
  'How do I start testing my app?',
  'Show me the bugs you found.',
]

/**
 * The Help entry point when the new-chat experience is enabled. Instead of opening the support
 * help widget, it explains that the in-app chat is how you ask questions about what the product
 * can do, and points to the Discord community for anything the chat can't cover.
 *
 * When a project chat is open, `onSelectExample` is provided so the example questions become
 * clickable — tapping one loads it into the chat and sends it (and closes the overlay).
 */
export default function ChatHelpOverlay({
  onClose,
  onSelectExample,
}: {
  onClose: () => void
  onSelectExample?: (text: string) => void
}) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <ModalPortal>
      <div className="chat-help-overlay" data-testid="chat-help-overlay" onClick={onClose}>
        <div
          className="chat-help"
          role="dialog"
          aria-labelledby="chat-help-title"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="chat-help__close"
            aria-label="Close"
            onClick={onClose}
            data-analytics-event="chat-help-close"
          >
            <X size={18} strokeWidth={2} aria-hidden />
          </button>

          <div className="chat-help__icon" aria-hidden>
            <MessageSquare size={22} strokeWidth={1.75} />
          </div>
          <h2 id="chat-help-title" className="chat-help__title">Need a hand?</h2>
          <p className="chat-help__lead">
            Just ask the chat. It's the fastest way to get help — you can ask what Replay QA can
            do, how a feature works, or what to try next, all in plain language.
            {onSelectExample && ' Tap an example below to ask it now.'}
          </p>

          <ul className="chat-help__examples">
            {EXAMPLE_PROMPTS.map((prompt) =>
              onSelectExample ? (
                <li key={prompt} className="chat-help__examples-item--interactive">
                  <button
                    type="button"
                    className="chat-help__example-btn"
                    onClick={() => {
                      trackSegmentEvent('Clicked help prompt', { prompt })
                      onSelectExample(prompt)
                    }}
                    data-testid="chat-help-example"
                    data-analytics-event="chat-help-example"
                  >
                    <Sparkles size={15} strokeWidth={1.75} aria-hidden />
                    <span>&ldquo;{prompt}&rdquo;</span>
                  </button>
                </li>
              ) : (
                <li key={prompt}>
                  <Sparkles size={15} strokeWidth={1.75} aria-hidden />
                  <span>&ldquo;{prompt}&rdquo;</span>
                </li>
              ),
            )}
          </ul>

          <p className="chat-help__discord-lead">
            Want to talk to a person or the wider community? Join us on Discord for additional
            support.
          </p>
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noreferrer"
            className="chat-help__discord"
            data-testid="chat-help-discord-link"
            data-analytics-event="chat-help-discord-link"
            onClick={() => {
              trackSegmentEvent('Clicked Join Discord', { location: 'help-menu' })
              // Discord opens in a new tab, so dismiss the overlay — otherwise it lingers over the
              // page and its full-screen backdrop swallows every subsequent click.
              onClose()
            }}
          >
            <DiscordIcon />
            Join the Discord community
          </a>
        </div>
      </div>
    </ModalPortal>
  )
}
